import React, { FC } from "react";

import clsx from "clsx";
import { Accordion, AccordionDetails, AccordionSummary, Hidden } from "@mui/material";

import { MinusIcon, PlusThinIcon } from "@/assets/icons";
import { useDetailPanels } from "@/components/organisms/DetailPanels/DetailPanels";

import InfoItem, { InfoItemProps } from "./InfoItem/InfoItem";
import { shouldBeHidden } from "./shouldBeHidden";
import { StyledDetailPanel } from "./styled";

type DetailPanelProps = {
    id: string;
    infoList: InfoItemProps[];
    title?: string;
    className?: string;
    subtitle?: string;
    detailPanelInfoGroup?: boolean;
    lastDetailPanelInGroup?: boolean;
    onClick?: () => void;
    dataTestId?: string;
};

const DetailPanel: FC<DetailPanelProps> = ({
    id,
    title,
    detailPanelInfoGroup = false,
    lastDetailPanelInGroup = false,
    subtitle,
    infoList,
    onClick,
    className,
    dataTestId,
}) => {
    const { onTogglePanelOpen, detailPanelOpen } = useDetailPanels();

    if (infoList.filter((item) => !shouldBeHidden(item)).length === 0) {
        return <></>;
    }

    const handleOpenPanel = () => onTogglePanelOpen(id);
    const isPanelOpen = detailPanelOpen === id;

    const clickable = !!onClick;

    const renderInfoList = infoList.map((infoItem) => <InfoItem {...infoItem} />);

    return (
        <StyledDetailPanel
            className={className}
            data-testid={dataTestId}
            $clickable={clickable}
            $detailPanelInfoGroup={detailPanelInfoGroup}
            $lastDetailPanelInGroup={lastDetailPanelInGroup}
        >
            <Hidden smUp>
                <Accordion
                    classes={{ root: "accordion-root", expanded: "accordion-expanded", rounded: "accordion-rounded" }}
                    expanded={isPanelOpen}
                    aria-controls="detailPanelInfo-content"
                    id="detailPanelInfo-header"
                    onChange={handleOpenPanel}
                    data-testid={`detailPanelInfo-accordion-${id}`}
                >
                    <AccordionSummary
                        classes={{
                            root: "accordion-summary-root",
                            content: "accordion-summary-content",
                            expandIconWrapper: "accordion-summary-expandIcon",
                        }}
                        className={clsx(!title && "hidden")}
                        expandIcon={isPanelOpen ? <MinusIcon /> : <PlusThinIcon />}
                    >
                        <p className="titlePanel">{title}</p>
                    </AccordionSummary>
                    <AccordionDetails classes={{ root: "accordion-details-root" }}>
                        <div className="contentPanel" onClick={clickable ? onClick : undefined}>
                            {subtitle && (
                                <div className="infoContainer">
                                    <span className="labelPanelSection">{subtitle}</span>
                                </div>
                            )}
                            {renderInfoList}
                        </div>
                    </AccordionDetails>
                </Accordion>
            </Hidden>

            <Hidden smDown>
                <p className={clsx("titlePanel", !title && "hidden")}>{title}</p>
                <div className={clsx("contentPanel", !title && "mt16")} onClick={clickable ? onClick : undefined}>
                    {subtitle && (
                        <div className="infoContainer">
                            <span className="labelPanelSection">{subtitle}</span>
                        </div>
                    )}
                    {renderInfoList}
                </div>
            </Hidden>
        </StyledDetailPanel>
    );
};

export default DetailPanel;
